import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Printer, ArrowLeft, Tag } from 'lucide-react';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = SECTIONS.preparation;

const TAG_META = {
  'CT-CONTRAST-RISK':         { label: 'Contrast risk', color: '#DC2626' },
  'METFORMIN':                { label: 'Metformin check', color: '#D97706' },
  'RENAL-FUNCTION-REQUIRED':  { label: 'eGFR required', color: '#DC2626' },
  'THYROID-CHECK':            { label: 'Thyroid check', color: '#D97706' },
  'BLOOD-THINNER':            { label: 'Blood thinner', color: '#DC2626' },
  'DIABETIC-STEROID-WARNING': { label: 'Diabetic steroid', color: '#D97706' },
  'DRIVER-REQUIRED':          { label: 'Driver required', color: '#7C3AED' },
  'FASTING':                  { label: 'Fasting', color: '#0891B2' },
  'FULL-BLADDER':             { label: 'Full bladder', color: '#0891B2' },
  'PREGNANCY-CHECK':          { label: 'Pregnancy check', color: '#EC4899' },
  'PROCEDURE-CONSENT':        { label: 'Consent', color: '#7C3AED' },
};

const MODALITY_ORDER = ['General', 'CT', 'Ultrasound', 'X-ray', 'DEXA', 'Dental', 'Special Patient Groups'];

const PRINT_CSS = `
  .pa-doc { color: #111827; font-family: 'Inter','Helvetica Neue',Arial,sans-serif; }
  .pa-cover { padding: 40px 32px; }
  .pa-cover h1 { font-size: 36px; font-weight: 800; color: #0d2644; margin: 0 0 6px 0; letter-spacing: -0.02em; }
  .pa-cover .pa-tag { color: #52c3c5; font-size: 13px; font-weight: 600; letter-spacing: 0.18em; text-transform: uppercase; }
  .pa-cover .pa-rule { height: 4px; width: 80px; background: linear-gradient(90deg,#0d2644 0%,#52c3c5 100%); border-radius: 2px; margin: 14px 0 22px 0; }
  .pa-cover h2 { font-size: 14px; font-weight: 700; color: #0d2644; margin: 22px 0 8px 0; text-transform: uppercase; letter-spacing: 0.08em; }
  .pa-cover .pa-toc { columns: 2; column-gap: 28px; font-size: 11px; color: #374151; line-height: 1.55; }
  .pa-cover .pa-toc-mod { font-weight: 700; color: #0d2644; margin-top: 6px; break-inside: avoid; }
  .pa-cover .pa-toc-mod:first-child { margin-top: 0; }
  .pa-cover .pa-toc li { list-style: none; padding-left: 8px; break-inside: avoid; }

  .pa-guide { padding: 26px 32px; page-break-before: always; }
  .pa-guide-head { border-bottom: 2px solid #0d2644; padding-bottom: 8px; margin-bottom: 14px; display: flex; justify-content: space-between; align-items: end; gap: 12px; }
  .pa-guide-head .pa-mod { font-size: 10px; color: #52c3c5; font-weight: 700; text-transform: uppercase; letter-spacing: 0.14em; margin-bottom: 2px; }
  .pa-guide-head .pa-title { font-size: 22px; font-weight: 700; color: #0d2644; margin: 0; line-height: 1.18; }
  .pa-guide-head .pa-num { font-size: 10px; color: #6b7280; font-weight: 600; }
  .pa-tags { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 12px; }
  .pa-tag-pill { font-size: 9px; font-weight: 700; padding: 2px 8px; border-radius: 999px; border: 1px solid; text-transform: uppercase; letter-spacing: 0.04em; }
  .pa-body { font-size: 11px; line-height: 1.55; color: #1f2937; }
  .pa-body h1 { font-size: 14px; color: #0d2644; margin: 14px 0 6px 0; font-weight: 700; }
  .pa-body h2 { font-size: 13px; color: #0d2644; margin: 14px 0 6px 0; font-weight: 700; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; }
  .pa-body h3 { font-size: 12px; color: #0d2644; margin: 10px 0 4px 0; font-weight: 700; }
  .pa-body p { margin: 4px 0; }
  .pa-body ul, .pa-body ol { margin: 4px 0 6px 0; padding-left: 18px; }
  .pa-body li { margin-bottom: 2px; }
  .pa-body strong { color: #111827; }
  .pa-body code { background: #f3f4f6; padding: 1px 4px; border-radius: 3px; font-size: 10px; }
  .pa-foot { margin-top: 16px; padding-top: 6px; border-top: 1px solid #e5e7eb; font-size: 8px; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.1em; display: flex; justify-content: space-between; }

  @media print {
    body { background: white !important; }
    .pa-toolbar { display: none !important; }
    .pa-doc { box-shadow: none !important; border: none !important; max-width: none !important; padding: 0 !important; }
    .pa-cover { padding: 14mm 12mm !important; page-break-after: always; }
    .pa-guide { padding: 10mm 12mm !important; page-break-before: always; }
    .pa-guide:last-of-type { page-break-after: auto; }
    @page { size: A4; margin: 0; }
  }
`;

// Friendly slug for the print TOC anchor.
const slugId = (s) => 'g-' + (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);

export default function PreparationPrintAllPage() {
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/prep-guides`, { withCredentials: true })
      .then(r => setGuides(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // Group + sort by modality, then alphabetical title.
  const grouped = MODALITY_ORDER.map(m => ({
    modality: m,
    items: guides
      .filter(g => g.modality === m)
      .sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.title.localeCompare(b.title)),
  })).filter(g => g.items.length > 0);

  // Append any modality that's not in the canonical order at the end.
  const knownMods = new Set(MODALITY_ORDER);
  const extraMods = [...new Set(guides.map(g => g.modality))].filter(m => !knownMods.has(m));
  for (const m of extraMods) {
    grouped.push({
      modality: m,
      items: guides.filter(g => g.modality === m).sort((a, b) => a.title.localeCompare(b.title)),
    });
  }

  // Flat ordered list for global numbering
  const flat = grouped.flatMap(g => g.items.map(it => ({ ...it, _modality: g.modality })));

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading patient prep guides…</div>;
  }

  const today = new Date().toLocaleDateString('en-AU', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white" data-testid="prep-print-all-page">
      <style>{PRINT_CSS}</style>

      {/* Toolbar (screen only) */}
      <div className="pa-toolbar sticky top-0 z-10 border-b bg-white px-6 py-3 flex items-center justify-between gap-3" style={{ borderColor: '#e5e7eb' }}>
        <Link to="/preparation" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="prep-print-all-back">
          <ArrowLeft className="w-4 h-4" /> Back to library
        </Link>
        <div className="text-xs text-muted-foreground hidden sm:block">
          Bundled view of all <span className="font-semibold text-foreground">{guides.length}</span> patient preparation guides — ready to print or save as a single PDF.
        </div>
        <button onClick={() => window.print()} data-testid="prep-print-all-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg"
          style={{ backgroundColor: P.color }}>
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
      </div>

      <div className="pa-doc max-w-[1100px] mx-auto">
        {/* Cover page with TOC */}
        <section className="pa-cover">
          <span className="pa-tag">i3 Radiology · Patient Preparation Bundle</span>
          <h1>All patient preparation guides</h1>
          <div className="pa-rule" />
          <p style={{ fontSize: 12, color: '#374151', marginBottom: 4 }}>
            <strong>{guides.length}</strong> guides · {grouped.length} modalit{grouped.length === 1 ? 'y' : 'ies'}
          </p>
          <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>
            Generated on {today}. Refer to your facility's RIS for the latest version. Patient-facing fasting / driver / contrast instructions follow individual exam protocols.
          </p>

          <h2>Contents</h2>
          <div className="pa-toc">
            {grouped.map(g => (
              <div key={g.modality}>
                <div className="pa-toc-mod">{g.modality} ({g.items.length})</div>
                <ul style={{ margin: '2px 0 6px 0', padding: 0 }}>
                  {g.items.map((it, idx) => {
                    const num = flat.findIndex(f => f.guide_id === it.guide_id) + 1;
                    return (
                      <li key={it.guide_id}>
                        <span style={{ color: '#9ca3af', marginRight: 4 }}>#{num}</span>
                        <a href={`#${slugId(it.guide_id || it.title)}`} style={{ color: '#0d2644', textDecoration: 'none' }}>
                          {it.title}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* One page per guide, page-break-before */}
        {flat.map((g, idx) => {
          const tagBlock = (g.alerts || []).map(t => {
            const m = TAG_META[t] || { label: t, color: '#64748B' };
            return (
              <span key={t} className="pa-tag-pill" style={{ color: m.color, borderColor: `${m.color}55`, backgroundColor: `${m.color}10` }}>
                <Tag className="inline w-2.5 h-2.5 mr-0.5" style={{ verticalAlign: '-1px' }} />
                {m.label}
              </span>
            );
          });

          return (
            <section key={g.guide_id} id={slugId(g.guide_id || g.title)} className="pa-guide">
              <div className="pa-guide-head">
                <div>
                  <div className="pa-mod">{g._modality}{g.subcategory ? ` · ${g.subcategory}` : ''}</div>
                  <h2 className="pa-title">{g.title}</h2>
                </div>
                <span className="pa-num">#{idx + 1} of {flat.length}</span>
              </div>
              {tagBlock.length > 0 && <div className="pa-tags">{tagBlock}</div>}
              <div className="pa-body">
                <ReactMarkdown>{g.content || ''}</ReactMarkdown>
              </div>
              <div className="pa-foot">
                <span>i3 Radiology · Patient Preparation</span>
                <span>{g.guide_id}</span>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
